import React from 'react';
import { OrderType } from '../../../../../types/types';
import { orderTypeDisplayName } from '../../../../../types/constants';
import { Button } from '../../../../ui/button';
import cn from '../../../../../lib/utils';

/**
 * Interface representing the props for the OrderButton component.
 *
 * orderType - The type of order to apply (e.g., newest, active).
 * currentOrder - The currently selected order type to determine if this button is active.
 * setQuestionOrder - A function that sets the order of questions based on the order type.
 */
interface OrderButtonProps {
  orderType: OrderType;
  currentOrder: OrderType;
  setQuestionOrder: (order: OrderType) => void;
}

/**
 * OrderButton component renders a button that, when clicked, triggers the setQuestionOrder function
 * with the provided order type.
 * It will update the order of questions based on the input order type.
 *
 * @param orderType - The order type to apply when the button is clicked.
 * @param currentOrder - The currently selected order type.
 * @param setQuestionOrder - Callback function to set the order of questions based on the order type.
 */
const OrderButton = ({ orderType, currentOrder, setQuestionOrder }: OrderButtonProps) => {
  // Check if this button is for the current order type (to highlight it)
  const isActive = currentOrder === orderType;

  return (
    <Button
      variant='ghost'
      size='sm'
      className={cn(
        'px-2 py-1 text-xs rounded-md',
        isActive ? 'bg-primary/10 text-primary' : 'hover:bg-gray-100 text-muted-foreground',
      )}
      onClick={() => {
        setQuestionOrder(orderType);
      }}>
      {orderTypeDisplayName[orderType]}
    </Button>
  );
};

export default OrderButton;
